import { Icon } from "@pamperedchef/design-system";
import { className as classNameHelper } from "@/composites/foundation/utilities/className";
import type { BaseHit, Hit } from "instantsearch.js";
import type { SendEventForHits } from "instantsearch.js/es/lib/utils";
import React, {
	Fragment,
	useCallback,
	useEffect,
	useRef,
	useState
} from "react";

import { useAutocomplete } from "./useAutocomplete";

import * as styles from "./Search.module.scss";

interface HitProps {
	hit: Hit<BaseHit>;
	sendEvent: SendEventForHits;
}

/**
 * Hit component
 */
function Hit({ hit }: HitProps): JSX.Element {
	return (
		<span
			dangerouslySetInnerHTML={{
				__html: Array.isArray(hit?._highlightResult?.query)
					? hit._highlightResult?.query?.[0]?.value
					: (hit._highlightResult?.query?.value ?? "")
			}}
		/>
	);
}

/**
 * Redirect to the search results page when a hit is selected
 */
function handleHitSelection(query: string | undefined): void {
	if (!query) return;

	document.location =
		"/search-browse-results?searchText=" + encodeURIComponent(query);
}

type Decorator = (Element: JSX.Element) => JSX.Element;

export type SearchProps = React.HTMLAttributes<HTMLElement> &
	WithDecoratorProps & {
		inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
	};

interface WithDecoratorProps {
	decorator?: Decorator;
}

/**
 * Decorator support HOC
 */
function withDecorator<T extends WithDecoratorProps = WithDecoratorProps>(
	WrappedComponent: React.ComponentType<Omit<T, "decorator">>
): React.ComponentType<T> {
	/**
	 * Wrapped component with decorator applied
	 */
	function DecoratedComponent({ decorator, ...props }: T): JSX.Element {
		if (decorator) {
			return decorator(<WrappedComponent {...props} />);
		}

		return <WrappedComponent {...props} />;
	}

	return DecoratedComponent;
}

/**
 * Search component
 */
function SearchComponent({
	className,
	inputProps,
	...props
}: SearchProps): JSX.Element {
	const { indices, currentRefinement, refine } = useAutocomplete();
	const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
	const [isFocused, setIsFocused] = useState<boolean>(false);
	const listRef = useRef<HTMLUListElement>(null);

	// Handle keyboard events for navigation
	const handleKeyDown = useCallback(
		(event: React.KeyboardEvent<HTMLInputElement>): void => {
			const hits = indices.flatMap((index) => index.hits);

			switch (event.key) {
				case "ArrowDown":
					event.preventDefault();
					setHighlightedIndex((prev) => (prev + 1) % hits.length);
					break;
				case "ArrowUp":
					event.preventDefault();
					setHighlightedIndex(
						(prev) => (prev - 1 + hits.length) % hits.length
					);
					break;
				case "Enter":
					if (highlightedIndex >= 0 && hits[highlightedIndex]) {
						const selectedHit = hits[highlightedIndex];

						handleHitSelection(selectedHit.objectID);
					}

					break;
				default:
					break;
			}
		},
		[indices, highlightedIndex]
	);

	// Handle form submission
	const handleSubmit = useCallback(
		(event: React.FormEvent<HTMLFormElement>): void => {
			event.preventDefault();

			handleHitSelection(currentRefinement ?? "");
		},
		[currentRefinement]
	);

	/**
	 * Handle option selection via click
	 * @param query The suggested query
	 */
	function handleOptionClick(query: string): void {
		handleHitSelection(query);
	}

	// Scroll to the highlighted item when navigation occurs
	useEffect(() => {
		if (highlightedIndex >= 0 && listRef.current) {
			const listItem = listRef.current.children[
				highlightedIndex
			] as HTMLLIElement;

			listItem?.scrollIntoView({ block: "nearest" });
		}
	}, [highlightedIndex]);

	// Determine whether to show the list
	const shouldShowList =
		isFocused && (currentRefinement?.length ?? 0) > 0 && indices.length > 0;

	return (
		<div {...props} {...classNameHelper(styles["container"], className)}>
			<form className={styles["input-wrapper"]} onSubmit={handleSubmit}>
				<input
					value={currentRefinement}
					onChange={(event) => refine(event.currentTarget.value)}
					onKeyDown={handleKeyDown}
					onFocus={() => setIsFocused(true)}
					onBlur={() => setIsFocused(false)}
					aria-autocomplete="list"
					aria-haspopup="listbox"
					{...classNameHelper(
						styles["input"],
						shouldShowList ? styles["input--open"] : ""
					)}
					{...inputProps}
				/>
				<button
					type="submit"
					className={styles["submit"]}
					aria-label="Search"
				>
					<Icon name="search" size={16} />
				</button>
			</form>
			{shouldShowList && (
				<ul ref={listRef} role="listbox" className={styles["list"]}>
					{indices.map((index) => (
						<Fragment key={index.indexName}>
							{index.hits.map((hit, idx) => (
								<li
									key={hit.objectID}
									{...classNameHelper(
										styles["list-item"],
										highlightedIndex === idx
											? styles["list-item--highlighted"]
											: ""
									)}
									role="option"
									aria-selected={highlightedIndex === idx}
									onMouseDown={() =>
										handleOptionClick(hit.objectID)
									}
								>
									<Hit
										hit={hit}
										sendEvent={index.sendEvent}
									/>
								</li>
							))}
						</Fragment>
					))}
				</ul>
			)}
		</div>
	);
}

export const Search = withDecorator<SearchProps>(SearchComponent);
